import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  // Manual loading uses the same page cache and card rendering as infinite scroll.
  #manualLoading = false;
  #pageGeneration = 0;
  #renderingNext = false;
  #renderingPrevious = false;
  #manualObserver = new MutationObserver(() => this.#syncLoadMoreButtons());

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    if (this.getAttribute('infinite-scroll') !== 'false' && !this.#isManual) {
      this.#fetchPage('next').catch(console.error);
      this.#fetchPage('previous').catch(console.error);
    }
    this.#observeViewMore();
    this.addEventListener('click', this.#handleLoadMoreClick);
    this.#observeManualControls();

    // Listen for filter updates to clear cached pages
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#pageGeneration++;
    this.pages.clear();
    this.infinityScrollObserver?.disconnect();
    this.#observeViewMore();
    this.#observeManualControls();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#pageGeneration++;
    this.#manualObserver.disconnect();
    this.removeEventListener('click', this.#handleLoadMoreClick);
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listeners
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  get #isManual() {
    return this.getAttribute('loading-mode') === 'load_more';
  }

  #observeManualControls() {
    this.#manualObserver.disconnect();
    if (!this.#isManual) return;
    this.#manualObserver.observe(this, { childList: true, subtree: true });
    this.#syncLoadMoreButtons();
  }

  #syncLoadMoreButtons() {
    if (!this.#isManual) return;
    const controls = this.querySelector('[data-load-more-controls]');
    if (!controls) return;
    for (const button of controls.querySelectorAll('a[data-load-more]')) {
      const type = button.dataset.loadMore === 'previous' ? 'previous' : 'next';
      const page = this.#getPage(type);
      const available = this.#shouldUsePage(page);
      button.hidden = !available;
      if (available && page) button.href = page.url.toString();
      if (this.#manualLoading) button.setAttribute('aria-disabled', 'true');
      else button.removeAttribute('aria-disabled');
    }
  }

  /** @param {MouseEvent} event */
  #handleLoadMoreClick = async (event) => {
    if (!this.#isManual || !(event.target instanceof Element)) return;
    const button = event.target.closest('a[data-load-more]');
    if (!(button instanceof HTMLAnchorElement) || button.closest('results-list') !== this) return;
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (this.#manualLoading || button.hidden) return;

    const generation = this.#pageGeneration;
    const grid = this.refs.grid;
    const status = this.querySelector('[data-load-more-status]');
    const label = button.dataset.label || button.textContent.trim();
    this.#manualLoading = true;
    button.textContent = 'Loading…';
    grid?.setAttribute('aria-busy', 'true');
    if (status) status.textContent = '';
    this.#syncLoadMoreButtons();

    try {
      if (button.dataset.loadMore === 'previous') await this.#renderPreviousPage();
      else await this.#renderNextPage();
      if (generation === this.#pageGeneration && status) {
        //status.textContent = 'Products loaded.';
        status.textContent = '';
      }
    } catch (error) {
      if (generation === this.#pageGeneration && status) {
        status.textContent = 'Could not load products. Please try again.';
      }
      console.error('Load more products:', error);
    } finally {
      this.#manualLoading = false;
      grid?.removeAttribute('aria-busy');
      button.textContent = label;
      this.#syncLoadMoreButtons();
    }
  };

  #observeViewMore() {
    if (this.#isManual || this.getAttribute('infinite-scroll') === 'false') return;
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage().catch(console.error);
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage().catch(console.error);
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);
    if (!page || !this.#shouldUsePage(page)) return;
    await this.#fetchSpecificPage(page.page, page.url);
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const generation = this.#pageGeneration;
    const grid = this.refs.grid;
    // The renderer adds request-only query parameters to its URL object.
    // Give it a copy so those parameters cannot leak into links/history.
    const requestUrl = pageInfo.url ? new URL(pageInfo.url.toString()) : undefined;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, requestUrl);
    if (generation !== this.#pageGeneration || grid !== this.refs.grid || !this.isConnected) return;
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;
    if (!grid || this.#renderingNext) return;
    const nextPage = this.#getPage('next');
    if (!nextPage || !this.#shouldUsePage(nextPage)) return;

    this.#renderingNext = true;
    const generation = this.#pageGeneration;
    try {
      if (!this.pages.has(nextPage.page)) {
        await this.#fetchSpecificPage(nextPage.page, nextPage.url);
      }
      if (generation !== this.#pageGeneration || grid !== this.refs.grid || !this.isConnected) return;
      const elements = this.#getGridForPage(nextPage.page);
      if (!elements?.length) throw new Error('The next page did not contain product cards.');
      grid.append(...elements);
      this.#aspectRatioHelper?.processNewElements();
      await yieldToMainThread();
      if (generation !== this.#pageGeneration || !this.isConnected) return;
      history.pushState('', '', nextPage.url.toString());
      if (!this.#isManual) {
        requestIdleCallback(() => this.#fetchPage('next').catch(console.error));
      }
    } finally {
      this.#renderingNext = false;
    }
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;
    if (!grid || this.#renderingPrevious) return;
    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    this.#renderingPrevious = true;
    const generation = this.#pageGeneration;
    try {
      if (!this.pages.has(previousPage.page)) {
        await this.#fetchSpecificPage(previousPage.page, previousPage.url);
      }
      if (generation !== this.#pageGeneration || grid !== this.refs.grid || !this.isConnected) return;
      const elements = this.#getGridForPage(previousPage.page);
      if (!elements?.length) throw new Error('The previous page did not contain product cards.');

      const currentScrollTop = getScrollTop();
      const firstElement = grid.firstElementChild;
      const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + currentScrollTop : 0;
      grid.prepend(...elements);
      this.#aspectRatioHelper?.processNewElements();
      if (firstElement) {
        const newHeight = firstElement.getBoundingClientRect().top + getScrollTop();
        scrollTo({ top: currentScrollTop + newHeight - oldHeight, behavior: 'instant' });
      }
      await yieldToMainThread();
      if (generation !== this.#pageGeneration || !this.isConnected) return;
      history.pushState('', '', previousPage.url.toString());
      if (!this.#isManual) {
        requestIdleCallback(() => this.#fetchPage('previous').catch(console.error));
      }
    } finally {
      this.#renderingPrevious = false;
    }
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.delete('pb');
    url.searchParams.delete('section_id');
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    this.#pageGeneration++;
    this.pages.clear();

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        if (!this.#isManual && this.getAttribute('infinite-scroll') !== 'false') {
          this.#fetchPage('next').catch(console.error);
        }
        this.#syncLoadMoreButtons();
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
